import MainContainer from './components/MainContainer';
import PageLoadErrorDisplay from './components/PageLoadErrorDisplay/PageLoadErrorDisplay';
import { getEncompassToken } from './Service/AuthenticationService';
import { MdsProgressSpinner } from '@mds/react';
import { useEffect, useState } from 'react';

// uncomment the below two lines to enable the mock server for local development
// leave them commented while doing anything else (including running unit tests)
// public/mockServiceWorker.js is generated automatically by `npm run dev` — no manual `msw init` step needed.
// import { worker } from '../msw/worker';
// worker.start()

const APP_LOAD_TIMEOUT_MS = 10000;

function App() {
  const [isAuthCodeSet, setIsAuthCodeSet] = useState(!!sessionStorage.getItem('authCode'));
  const [isEncompassTokenSet, setIsEncompassTokenSet] = useState(false);
  const [hasLoadTimedOut, setHasLoadTimedOut] = useState(false);
  const [hasLoadFailed, setHasLoadFailed] = useState(false);
  // Temporary: track internal user access restriction during DynamoDB issue. Remove when resolved.
  const [isInternalAccessRestricted, setIsInternalAccessRestricted] = useState(false);

  /**
   * Checks for authCode in sessionStorage when the 'authCodeReady' event is fired
   * and updates the isAuthCodeSet state accordingly.
   */
  useEffect(() => {
    const handleReady = () => {
      const authCode = sessionStorage.getItem('authCode');
      setIsAuthCodeSet(!!authCode);
    };

    const handleAuthCodeError = () => {
      setHasLoadFailed(true);
    };

    // Temporary: listen for internal user access restriction during DynamoDB issue. Remove when resolved.
    const handleInternalAccessRestricted = () => {
      setIsInternalAccessRestricted(true);
    };

    window.addEventListener('authCodeReady', handleReady, { once: true });
    window.addEventListener('authCodeError', handleAuthCodeError, { once: true });
    window.addEventListener('internalAccessRestricted', handleInternalAccessRestricted, { once: true });

    return () => {
      window.removeEventListener('authCodeReady', handleReady);
      window.removeEventListener('authCodeError', handleAuthCodeError);
      window.removeEventListener('internalAccessRestricted', handleInternalAccessRestricted);
    };
  }, []);

  /**
   * Fetches the Encompass token using the authCode when isAuthCodeSet changes to true.
   * Updates the isEncompassTokenSet state if the token is successfully retrieved.
   */
  useEffect(() => {
    let isActive = true;

    const fetchData = async () => {
      if (!isAuthCodeSet || isEncompassTokenSet || hasLoadTimedOut || hasLoadFailed) {
        return;
      }

      try {
        const response = await getEncompassToken({
          authCode: sessionStorage.getItem('authCode') as string
        });

        const token = response?.response?.access_token;
        if (!isActive) {
          return;
        }

        if (token) {
          sessionStorage.setItem('encompassToken', token);
          setIsEncompassTokenSet(true);
        } else {
          setHasLoadFailed(true);
        }

        console.log('response from AuthenticationService:', response);
      } catch (error) {
        if (isActive) {
          setHasLoadFailed(true);
        }
        console.error('Error fetching data:', error);
      } finally {
        sessionStorage.removeItem('authCode');
      }
    };

    fetchData();

    return () => {
      isActive = false;
    };
  }, [isAuthCodeSet, isEncompassTokenSet, hasLoadTimedOut, hasLoadFailed]);

  useEffect(() => {
    if (isEncompassTokenSet || hasLoadTimedOut || hasLoadFailed) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setHasLoadTimedOut(true);
    }, APP_LOAD_TIMEOUT_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isEncompassTokenSet, hasLoadTimedOut, hasLoadFailed]);

  /**
   * Renders the MainContainer component if both authCode and Encompass token are set.
   */
  return (
    <div>
      {isEncompassTokenSet && !isInternalAccessRestricted ? (
        <MainContainer />
      ) : isInternalAccessRestricted ? (
        <PageLoadErrorDisplay message="Access for internal users is temporarily unavailable. We appreciate your patience while we work to resolve the issue." />
      ) : hasLoadTimedOut || hasLoadFailed ? (
        <PageLoadErrorDisplay />
      ) : (
        <MdsProgressSpinner data-testid="app-loading-spinner" open />
      )}
    </div>
  );
}

export default App;
